import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import * as d3 from 'd3';

interface SpendByVendorChartProps {
  data: any[];
}

const SpendByVendorChart: React.FC<SpendByVendorChartProps> = ({ data }) => {
  const theme = useTheme();
  const svgRef = useRef<SVGSVGElement>(null);
  const [processedData, setProcessedData] = useState<{ vendor: string; amount: number }[]>([]);

  useEffect(() => {
    if (!data || data.length === 0) {
      console.log('No data provided to SpendByVendorChart');
      return;
    }

    console.log('Processing vendor data for chart');
    
    // Process data by aggregating spend by vendor
    const vendorMap: Record<string, number> = {};
    
    data.forEach((item) => {
      if (item.supplier && item.amount) {
        const amount = typeof item.amount === 'number' ? item.amount : parseFloat(item.amount);
        if (!isNaN(amount)) {
          vendorMap[item.supplier] = (vendorMap[item.supplier] || 0) + amount;
        }
      }
    });

    // Convert to array and sort by amount (descending)
    const processedVendorData = Object.entries(vendorMap)
      .map(([vendor, amount]) => ({ vendor, amount }))
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 10); // Top 10 vendors

    console.log('Processed vendor data:', processedVendorData);
    setProcessedData(processedVendorData);
  }, [data]);

  useEffect(() => {
    if (processedData.length === 0 || !svgRef.current) {
      return;
    }

    console.log('Rendering SpendByVendorChart with data');
    
    // Clear previous chart
    d3.select(svgRef.current).selectAll("*").remove();

    // Set dimensions
    const margin = { top: 20, right: 30, bottom: 70, left: 60 };
    const width = svgRef.current.clientWidth - margin.left - margin.right;
    const height = svgRef.current.clientHeight - margin.top - margin.bottom;

    // Create SVG
    const svg = d3.select(svgRef.current)
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    // X scale
    const x = d3.scaleBand()
      .domain(processedData.map(d => d.vendor))
      .range([0, width])
      .padding(0.2);

    // Y scale
    const y = d3.scaleLinear()
      .domain([0, d3.max(processedData, d => d.amount) || 0])
      .range([height, 0]);

    // Add X axis
    svg.append("g")
      .attr("transform", `translate(0,${height})`)
      .call(d3.axisBottom(x))
      .selectAll("text")
      .attr("transform", "translate(-10,0)rotate(-45)")
      .style("text-anchor", "end")
      .style("font-size", "10px");

    // Add Y axis
    svg.append("g")
      .call(d3.axisLeft(y).ticks(5).tickFormat(d => `$${d3.format(".2s")(d as number)}`));

    // Add Y axis label
    svg.append("text")
      .attr("transform", "rotate(-90)")
      .attr("y", 0 - margin.left)
      .attr("x", 0 - (height / 2))
      .attr("dy", "1em")
      .style("text-anchor", "middle")
      .style("font-size", "12px")
      .text("Total Spend ($)");

    // Create tooltip
    const tooltip = d3.select("body")
      .append("div")
      .attr("class", "d3-tooltip")
      .style("position", "absolute")
      .style("background-color", "white")
      .style("padding", "8px")
      .style("border-radius", "4px")
      .style("box-shadow", "0 2px 5px rgba(0,0,0,0.2)")
      .style("pointer-events", "none")
      .style("opacity", 0)
      .style("z-index", 1000);

    // Add bars
    svg.selectAll(".bar")
      .data(processedData)
      .enter()
      .append("rect")
      .attr("class", "bar")
      .attr("x", d => x(d.vendor) || 0)
      .attr("width", x.bandwidth())
      .attr("y", d => y(d.amount))
      .attr("height", d => height - y(d.amount))
      .attr("fill", theme.palette.primary.main)
      .style("cursor", "pointer")
      .on("mouseover", (event, d) => {
        tooltip.transition()
          .duration(200)
          .style("opacity", 0.9);
        
        tooltip.html(`
          <strong>${d.vendor}</strong><br/>
          $${d.amount.toLocaleString()}
        `)
          .style("left", (event.pageX + 10) + "px")
          .style("top", (event.pageY - 28) + "px");
      })
      .on("mouseout", () => {
        tooltip.transition()
          .duration(500)
          .style("opacity", 0);
      });

    // Clean up tooltip on unmount
    return () => {
      d3.select(".d3-tooltip").remove();
    };
  }, [processedData, theme]);

  return (
    <Box sx={{ height: 300, position: 'relative', width: '100%' }}>
      {processedData.length > 0 ? (
        <svg 
          ref={svgRef} 
          width="100%" 
          height="100%"
          style={{ overflow: 'visible' }}
        />
      ) : (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100%',
          border: '1px dashed #ccc', 
          borderRadius: 1
        }}>
          <Typography variant="body2" color="text.secondary">
            No vendor data available for visualisation
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default SpendByVendorChart; 